import { LoadingBox } from '@outbound-ai/ursa'
import Papa, { ParseError } from 'papaparse'
import { ChangeEvent, ChangeEventHandler, useEffect, useState } from 'react'
import { FaPlus } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { patchKvpMapKey, postKvpMapKey } from '../../claims/claimsApi'
import { useKvpMapKey } from '../../claims/claimsHooks'
import { IKvpMap, IReplacement } from '../settingsTypes'

const csvNewLine = '\n'
export const SettingsDataMaps = () => {
  const [keySought, setKeySought] = useState<string | undefined>()
  const [ordinalSought, setOrdinalSought] = useState<number>(-1)
  const [keyToCreate, setKeyToCreate] = useState<string>('new map')
  const [textAreaText, setTextAreaText] = useState<string>('')
  const [csvErrors, setCsvErrors] = useState<string[]>([])
  const [createNotEdit, setCreateNotEdit] = useState<boolean>(true)
  const [keysDefined, setKeysDefined] = useState<string[]>([])
  const [keyDisplayed, setKeyDisplayed] = useState<string | null>(null)
  const [textAreaDisabled, setTextAreaDisabled] = useState<boolean>(false)

  const { data: kvpKeyResponse } = useKvpMapKey(keySought)

  useEffect(() => {
    if (kvpKeyResponse) {
      setKeysDefined(kvpKeyResponse.allKeys.sort((s1: string, s2: string) => s1.localeCompare(s2)))
      if (!createNotEdit && kvpKeyResponse.thisMap && kvpKeyResponse.thisKey && kvpKeyResponse.thisKey == keySought) {
        setTextAreaText(csvEncodeMap(kvpKeyResponse.thisMap))
        setKeyDisplayed(kvpKeyResponse.thisKey)
        setTextAreaDisabled(false)
      }
    }
  }, [kvpKeyResponse])

  const onSelectCreateNewMap = () => {
    setOrdinalSought(-1)
    setKeySought(undefined)
    setCreateNotEdit(true)
    setTextAreaText('')
    setTextAreaDisabled(false)
  }

  const onSelectExistingMap = (key: string, ordinal: number) => {
    setTextAreaDisabled(true)
    setOrdinalSought(ordinal)
    setKeySought(key)
    setCreateNotEdit(false)
  }

  const onChangeOfMapCsv: ChangeEventHandler<HTMLTextAreaElement> = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaText(e.target.value)
    const parsedMap = parseMap(e.target.value)
    if (parsedMap.errors != null) setCsvErrors(parsedMap.errors)
    else {
      setCsvErrors([])
    }
  }

  const onChangeOfKeyToCreate: ChangeEventHandler<HTMLInputElement> = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyToCreate(e.target.value)
  }

  const csvEncodeMap = (kvpMap: IKvpMap): string => {
    return Papa.unparse(kvpMap.replacements, { header: false, columns: ['old', 'new'], newline: csvNewLine })
  }

  const parseMap = (csv: string): { kvpMap: IKvpMap | null; errors: string[] | null } => {
    const parsed = Papa.parse('old,new' + csvNewLine + csv, { header: true, newline: csvNewLine })
    const rows = parsed.data as { old: string; new: string }[]
    if (0 < parsed.errors?.length)
      return {
        kvpMap: null,
        errors: parsed.errors.map((parseError: ParseError) => `${parseError.message} at ${parseError.row}`),
      }
    const replacements: IReplacement[] = []
    rows.map((row) => {
      replacements.push({ old: row.old, new: row.new })
    })
    return {
      kvpMap: { replacements: replacements },
      errors: null,
    }
  }

  const putMap = () => {
    const parseResult = parseMap(textAreaText)
    if (parseResult.kvpMap != null) {
      if (createNotEdit && keyToCreate && 0 < keyToCreate.trim().length) {
        const keyTrimmed = keyToCreate.trim()
        setKeyToCreate(keyTrimmed)
        postKvpMapKey(keyTrimmed, parseResult.kvpMap).then(() => {
          setKeySought(keyTrimmed)
          setCreateNotEdit(false)
        })
      } else {
        if (kvpKeyResponse?.thisKey) {
          patchKvpMapKey(kvpKeyResponse.thisKey, parseResult.kvpMap).then(() => setKeySought(kvpKeyResponse.thisKey))
        } else console.error('bug: for editing, key in existing response should not be null.')
      }
    }
  }

  return (
    <Div>
      <div className="section-header">
        <h2>Data Maps</h2>
        <p>Create and edit the data maps to use when uploading a CSV file in File Center.</p>
      </div>
      {!kvpKeyResponse ? (
        <LoadingBox />
      ) : (
        <>
          <div className="section-select">
            {0 === keysDefined.length ? (
              <>
                <h3>No maps defined yet</h3>
              </>
            ) : (
              <>
                <h3>Choose one of these maps to work with</h3>
                <form>
                  {keysDefined.map((key, ordinal) => {
                    return (
                      <div key={ordinal.toString()}>
                        <input
                          type="radio"
                          name="keySelection"
                          key={ordinal}
                          id={ordinal.toString()}
                          defaultChecked={ordinal === ordinalSought}
                          onChange={() => onSelectExistingMap(key, ordinal)}
                        />
                        <label htmlFor={ordinal.toString()}>{key}</label>
                      </div>
                    )
                  })}
                  <div key={'-1'}>
                    <input
                      type="radio"
                      name="keySelection"
                      key="-1"
                      id="-1"
                      defaultChecked={-1 === ordinalSought}
                      onChange={onSelectCreateNewMap}
                    />
                    <label htmlFor="-1">
                      {' '}
                      <FaPlus /> create new
                    </label>
                  </div>
                </form>
              </>
            )}
          </div>
          <div>
            <h3>
              {createNotEdit ? (
                <>Create a new data mapping named &quot;{keyToCreate}&quot;</>
              ) : (
                <>Edit data mapping for data map &quot;{keyDisplayed}&quot;</>
              )}
            </h3>
            <div>
              {createNotEdit && (
                <>
                  <label>Name the new map: </label>
                  <input type="text" onChange={onChangeOfKeyToCreate} value={keyToCreate} />
                </>
              )}
            </div>
          </div>
          <div className="section-map">
            <textarea
              disabled={textAreaDisabled}
              value={textAreaText}
              onChange={onChangeOfMapCsv}
              wrap="off"
              spellCheck="false"
            />
          </div>
          <div className="section-actions">
            <p>
              <Button disabled={0 < csvErrors?.length} variant="info" width="100%" onClick={putMap}>
                {createNotEdit ? <> Create </> : <> Save </>}
              </Button>
            </p>
            {0 < csvErrors?.length && (
              <>
                <h4>The data map has errors.</h4>
                <p>
                  The map value in the text box must be &apos;comma separated values&apos; with two values on each line:
                  first the value to replace, then a comma, then the value to replace it with. (that&apos;s one comma in
                  the middle). Values that really have commas must be <i>escaped</i>
                  for example by putting the whole value in &quot;double quotes&quot;
                </p>
                <p>The first few errors are:</p>
                <ul>
                  {csvErrors.map((errorText, ordinal) => (
                    <li key={ordinal}>{errorText}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </Div>
  )
}

const Div = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 0.5rem;

  .section-header {
    height: fit-content;
  }
  .section-select {
    height: fit-content;
  }
  .section-map {
    flex-grow: 1;
  }
  .section-actions {
    height: fit-content;
  }
  textarea {
    height: 100%;
    width: 100%;
    resize: none;
    padding: 1rem;
    margin-top: 1rem;
  }
  .create-edit-button {
    margin-top: 2rem;
    padding: 0.75rem;
  }

  label {
    margin-right: 0.75rem;
    margin-left: 0.75rem;
  }
`
